import ChevronDown from "@/assets/icons/chevron-down.svg?react";
import ChevronUp from "@/assets/icons/chevron-up.svg?react";
import {
  MaxWidthContent,
  MaxWidthContentProps,
} from "@endya-conformite/ui-kit";
import classNames from "classnames";
import { useCallback, useContext, useEffect, useRef, useState } from "react";
import styles from "./Dialog.module.scss";
import { DialogContext } from "./DialogContext";

export type DialogComponentProps = Pick<DialogProps, "dialogRef">;

type DialogProps = {
  children: React.ReactNode;
  dialogRef: React.MutableRefObject<HTMLDialogElement | null>;
  variant?: "modal" | "floating";
  title?: string;
  disableMinimize?: boolean;
  resizable?: boolean;
  expandTriggerRef?: React.MutableRefObject<(() => void) | null>;
  className?: string;
  contentSize?: MaxWidthContentProps["size"];
};

const MIN_FLOATING_WIDTH = 320;
const MIN_FLOATING_HEIGHT = 240;

export function Dialog({
  children,
  dialogRef,
  variant = "modal",
  title,
  disableMinimize,
  resizable,
  expandTriggerRef,
  className,
  contentSize,
}: DialogProps) {
  const [isMinimized, setIsMinimized] = useState(false);
  const [size, setSize] = useState<{ width: number; height: number } | null>(
    null
  );
  const resizeStartRef = useRef<{
    startX: number;
    startY: number;
    startWidth: number;
    startHeight: number;
  } | null>(null);

  const handleResizePointerDown = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      const dialogElement = dialogRef.current;
      if (!dialogElement) return;
      e.preventDefault();
      const rect = dialogElement.getBoundingClientRect();
      resizeStartRef.current = {
        startX: e.clientX,
        startY: e.clientY,
        startWidth: rect.width,
        startHeight: rect.height,
      };
      e.currentTarget.setPointerCapture(e.pointerId);
    },
    [dialogRef]
  );

  const handleResizePointerMove = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      const start = resizeStartRef.current;
      if (!start) return;
      const deltaX = start.startX - e.clientX;
      const deltaY = start.startY - e.clientY;
      const maxWidth = window.innerWidth - 48;
      const maxHeight = window.innerHeight - 24;
      const nextWidth = Math.min(
        Math.max(start.startWidth + deltaX, MIN_FLOATING_WIDTH),
        maxWidth
      );
      const nextHeight = Math.min(
        Math.max(start.startHeight + deltaY, MIN_FLOATING_HEIGHT),
        maxHeight
      );
      setSize({ width: nextWidth, height: nextHeight });
    },
    []
  );

  const handleResizePointerUp = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      resizeStartRef.current = null;
      if (e.currentTarget.hasPointerCapture(e.pointerId)) {
        e.currentTarget.releasePointerCapture(e.pointerId);
      }
    },
    []
  );

  useEffect(() => {
    if (expandTriggerRef) {
      expandTriggerRef.current = () => setIsMinimized(false);
    }
  });

  useEffect(() => {
    if (variant === "floating") return;
    const dialogElement = dialogRef.current;
    dialogElement?.addEventListener("click", (e) => {
      if (e.target === dialogRef.current) {
        dialogRef.current?.close();
      }
    });
    return () => dialogElement?.removeEventListener("click", () => null);
  }, [dialogRef, variant]);

  if (variant === "floating") {
    const floatingStyle =
      resizable && size && !isMinimized
        ? { width: `${size.width}px`, height: `${size.height}px` }
        : undefined;
    return (
      <dialog
        ref={dialogRef}
        className={classNames(
          styles.floating,
          { [styles.floatingResizable]: resizable },
          className
        )}
        style={floatingStyle}
      >
        {resizable && !isMinimized && (
          <div
            className={styles.resizeHandle}
            onPointerDown={handleResizePointerDown}
            onPointerMove={handleResizePointerMove}
            onPointerUp={handleResizePointerUp}
            onPointerCancel={handleResizePointerUp}
            role="separator"
            aria-label="Redimensionner"
          />
        )}
        <button
          type="button"
          className={styles.floatingMinimizeBtn}
          onClick={() => !disableMinimize && setIsMinimized((v) => !v)}
          aria-expanded={!isMinimized}
          aria-label={isMinimized ? "Agrandir" : "Réduire"}
          disabled={disableMinimize}
        >
          {isMinimized ? <ChevronUp /> : <ChevronDown />}
        </button>
        <div className={styles.floatingBody} data-minimized={isMinimized}>
          {children}
        </div>
      </dialog>
    );
  }

  return (
    <dialog ref={dialogRef} className={classNames(styles.dialog, className)}>
      <MaxWidthContent className={styles.content} size={contentSize}>
        {children}
      </MaxWidthContent>
    </dialog>
  );
}

export function DialogContainer() {
  const { dialogState } = useContext(DialogContext);
  return dialogState ? <dialogState.component {...dialogState.props} /> : null;
}
